// Points de rupture responsive pour les styles inline (où les media queries CSS sont impossibles).
import { useEffect, useState } from "react";

export const BREAKPOINTS = {
  /** < 640px : téléphone */
  mobile: 640,
  /** < 1024px : tablette / petit portable */
  tablet: 1024,
} as const;

export interface Breakpoint {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  /** Mobile OU tablette : espace horizontal limité */
  isCompact: boolean;
}

function compute(width: number): Breakpoint {
  const isMobile = width < BREAKPOINTS.mobile;
  const isTablet = !isMobile && width < BREAKPOINTS.tablet;
  return { isMobile, isTablet, isDesktop: !isMobile && !isTablet, isCompact: isMobile || isTablet };
}

const currentWidth = () => (typeof window === "undefined" ? 1280 : window.innerWidth);

/** Vrai tant que la media query correspond, ex. useMediaQuery("(max-width: 1199px)"). */
export function useMediaQuery(query: string): boolean {
  const get = () => typeof window !== "undefined" && window.matchMedia(query).matches;
  const [matches, setMatches] = useState(get);

  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}

/** Re-rend uniquement quand on change de palier (pas à chaque pixel). */
export function useBreakpoint(): Breakpoint {
  const [bp, setBp] = useState(() => compute(currentWidth()));

  useEffect(() => {
    let frame = 0;
    const onResize = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const next = compute(currentWidth());
        setBp((prev) =>
          prev.isMobile === next.isMobile && prev.isTablet === next.isTablet ? prev : next,
        );
      });
    };
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return bp;
}
